import React, { useState } from 'react';
import {
  Box,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Divider,
} from '@mui/material';
import { CloudDone, LinkOff, Sync } from '@mui/icons-material';

/** Official Google Drive triangle logo rendered as an inline SVG */
function DriveIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 87.3 78"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
        fill="#0066da"
      />
      <path
        d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
        fill="#00ac47"
      />
      <path
        d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.5l5.85 11.5z"
        fill="#ea4335"
      />
      <path
        d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
        fill="#00832d"
      />
      <path
        d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
        fill="#2684fc"
      />
      <path
        d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
        fill="#ffba00"
      />
    </svg>
  );
}

/**
 * DriveStatusButton
 *
 * Shows the Google Drive logo with a green (connected) or red (disconnected) dot.
 * - Not connected → clicking opens DrivePermissionDialog via onConnect()
 * - Connected      → clicking opens a small menu with sync status + disconnect option
 */
export default function DriveStatusButton({ driveConnected, onConnect, onDisconnect }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (e) => {
    if (driveConnected) {
      setAnchorEl(e.currentTarget);
    } else {
      onConnect();
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleDisconnect = () => {
    handleClose();
    onDisconnect?.();
  };

  const tooltipTitle = driveConnected ? 'Google Drive: Syncing' : 'Connect Google Drive';

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label={tooltipTitle}
          sx={{ position: 'relative', p: '6px' }}
        >
          <DriveIcon size={22} />
          {/* Status dot */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 4,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: driveConnected ? '#34A853' : '#ea4335',
              border: '1.5px solid',
              borderColor: 'background.paper',
            }}
          />
        </IconButton>
      </Tooltip>

      {/* Connected: popover menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
      >
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <ListItemIcon>
            <CloudDone fontSize="small" sx={{ color: '#34A853' }} />
          </ListItemIcon>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Google Drive
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sync enabled
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem disabled sx={{ opacity: '1 !important', py: 0.5 }}>
          <ListItemIcon>
            <Sync fontSize="small" sx={{ color: 'text.disabled' }} />
          </ListItemIcon>
          <Typography variant="caption" color="text.secondary">
            PDFs, notes &amp; progress synced
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleDisconnect}>
          <ListItemIcon>
            <LinkOff fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Disconnect Drive</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
